import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { deleteAccount, getMe } from '../api';
import { signOut } from '../auth';
import { useSession } from '../auth';
import type { User } from '../types';
import { Spinner } from '../components/Spinner';

export function Settings() {
  const navigate = useNavigate();
  const { session } = useSession();
  const [user, setUser] = useState<User | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  useEffect(() => {
    getMe()
      .then(setUser)
      .catch(() => {
        /* non-fatal — Settings can render without /users/me */
      });
  }, []);

  const onSignOut = async (): Promise<void> => {
    setSigningOut(true);
    setSignOutError(null);
    try {
      await signOut();
      navigate('/sign-in', { replace: true });
    } catch (e) {
      // Keep the user here with a retry rather than leaving them half-signed-out.
      setSignOutError(e instanceof Error ? e.message : 'Failed to sign out');
      setSigningOut(false);
    }
  };

  const onConfirmDelete = async (): Promise<void> => {
    setDeleting(true);
    setError(null);
    // Three distinct outcomes — each gets its own banner so the user (and we)
    // can tell from the message alone what actually happened:
    //  1. Server confirmed full deletion         → "Account deleted."
    //  2. Server confirmed partial / failed delete → "Account deletion DID NOT complete..."
    //  3. Request never reached server (network/CORS/timeout) → "Could not reach the server..."
    let banner = 'Account deleted.';
    try {
      const result = await deleteAccount();
      if (!result.deleted) {
        banner = `Account deletion DID NOT complete on the server. ${result.error ?? ''} Sign in again and retry, or contact support.`.trim();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      banner = `Could not reach the server to delete your account (${msg}). Your data has NOT been removed. Sign in again and retry.`;
    } finally {
      // Per spec: always sign out client-side. Local session can't prove anything
      // about what's stored server-side; signing out forces a fresh load on retry.
      // Best-effort — a failed remote revoke shouldn't block navigating away
      // with the deletion-outcome banner.
      try {
        await signOut();
      } catch {
        // ignore — see comment above
      }
      navigate('/sign-in', { replace: true, state: { banner } });
    }
  };

  return (
    <div className="shell">
      <header className="rise" style={{ marginBottom: 8 }}>
        <p className="eyebrow">Account</p>
        <h1 className="h1">Settings</h1>
      </header>

      <section className="section rise">
        <div className="card">
          <p className="eyebrow">Signed in as</p>
          <p className="h3" style={{ marginTop: 4 }}>
            {user?.email ?? session?.user.email ?? '—'}
          </p>
          {user?.name ? (
            <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>{user.name}</p>
          ) : null}
        </div>
        {signOutError ? <div className="errorbar">{signOutError}</div> : null}
        <button
          type="button"
          className="btn btn--ghost btn--full"
          disabled={signingOut}
          onClick={() => void onSignOut()}
        >
          {signingOut ? (<><Spinner /> Signing out…</>) : 'Sign out'}
        </button>
        <Link to="/terms" className="btn btn--ghost btn--full" style={{ textAlign: 'center' }}>
          Terms of Service
        </Link>
      </section>

      <section className="section rise">
        <h2 className="h2" style={{ color: 'var(--danger)' }}>Danger zone</h2>
        {!confirmOpen ? (
          <>
            <p className="muted" style={{ fontSize: 14, lineHeight: 1.55 }}>
              Permanently delete your account and everything in it.
            </p>
            <button
              type="button"
              className="btn btn--full"
              style={{
                background: 'transparent',
                color: 'var(--danger)',
                borderColor: 'rgba(248, 113, 113, 0.45)',
              }}
              onClick={() => setConfirmOpen(true)}
            >
              Delete my account
            </button>
          </>
        ) : (
          <div
            className="card"
            style={{ borderColor: 'rgba(248, 113, 113, 0.45)' }}
          >
            <p className="h3">Are you sure?</p>
            <p className="muted" style={{ marginTop: 8, lineHeight: 1.55, fontSize: 14 }}>
              This will permanently delete your account, profile, check-ins, chat history,
              and coaching memory. This cannot be undone.
            </p>
            <p className="muted" style={{ marginTop: 12, fontSize: 13 }}>
              Type <strong style={{ color: 'var(--text)' }}>DELETE</strong> to confirm.
            </p>
            <input
              className="input"
              style={{ marginTop: 8 }}
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              autoFocus
              autoComplete="off"
              autoCapitalize="characters"
            />
            {error ? <div className="errorbar" style={{ marginTop: 12 }}>{error}</div> : null}
            <div className="row" style={{ marginTop: 16, gap: 8 }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setConfirmOpen(false);
                  setConfirmInput('');
                  setError(null);
                }}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                style={{
                  background: 'var(--danger)',
                  color: '#1f0707',
                  borderColor: 'transparent',
                  flex: 1,
                }}
                disabled={deleting || confirmInput !== 'DELETE'}
                onClick={() => void onConfirmDelete()}
              >
                {deleting ? (<><Spinner /> Deleting…</>) : 'Delete forever'}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
